import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { KpiStrip } from "@/components/KpiStrip";
import { MoveInDialog } from "@/components/MoveInDialog";
import { EnquiryDialog, EnquiryDetailDialog } from "@/pages/Waitlist";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { 
  CalendarClock, Plus, ChevronLeft, ChevronRight, Lock, Clock, Trash2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// 30-Minute Time Slots (08:00 AM to 05:30 PM)
const TIME_SLOTS = [
  "08:00 AM", "08:30 AM",
  "09:00 AM", "09:30 AM",
  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "01:00 PM", "01:30 PM",
  "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM",
  "04:00 PM", "04:30 PM",
  "05:00 PM", "05:30 PM"
];

const getWeekDays = (baseDate) => {
  const monday = new Date(baseDate);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const formatDateYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const slotIdx = (t) => TIME_SLOTS.indexOf(t);

const blockCovers = (b, date, slot) => {
  if (b.date !== date) return false;
  const s = slotIdx(b.start_time), e = slotIdx(b.end_time), i = slotIdx(slot);
  return i >= s && i <= e;
};

export default function Overview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const restrictedKey = user && user.site_access && user.site_access !== "all" ? user.site_access.toUpperCase() : null;
  const [stats, setStats] = useState(null);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [currentWeekBase, setCurrentWeekBase] = useState(new Date());

  // Block time dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockTitle, setBlockTitle] = useState("");
  const [blockDate, setBlockDate] = useState(formatDateYMD(new Date()));
  const [blockStartTime, setBlockStartTime] = useState("09:00 AM");
  const [blockEndTime, setBlockEndTime] = useState("12:00 PM");
  const [sizes, setSizes] = useState([]);
  const [quickAdd, setQuickAdd] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [overdue, setOverdue] = useState(null);

  const loadStats = useCallback(() => api.getStats().then(setStats).catch(() => {}), []);
  const loadWaitlist = useCallback(() => {
    api.getWaitlist().then((list) => {
      setWaitlistEntries(list || []);
    }).catch(() => {});
  }, []);

  const loadBlockedTimes = useCallback(() => {
    api.getBlockedTimes()
      .then((res) => setBlockedSlots(res || []))
      .catch(() => setBlockedSlots([]));
  }, []);

  useEffect(() => { 
    loadStats(); 
    loadWaitlist(); 
    loadBlockedTimes(); 
    api.getSizes().then(setSizes).catch(() => {});
    api.getOverdueSummary().then(setOverdue).catch(() => {});
  }, [loadStats, loadWaitlist, loadBlockedTimes]);

  const weekDays = useMemo(() => getWeekDays(currentWeekBase), [currentWeekBase]);

  const prevWeek = () => {
    const d = new Date(currentWeekBase);
    d.setDate(d.getDate() - 7);
    setCurrentWeekBase(d);
  };

  const nextWeek = () => {
    const d = new Date(currentWeekBase);
    d.setDate(d.getDate() + 7);
    setCurrentWeekBase(d);
  };

  const todayStr = formatDateYMD(new Date());

  const handleAddBlock = async (e) => {
    e.preventDefault();
    if (!blockTitle.trim()) return;
    if (slotIdx(blockEndTime) < slotIdx(blockStartTime)) {
      toast.error("End time must be after start time");
      return;
    }
    try {
      await api.addBlockedTime({
        title: blockTitle,
        date: blockDate,
        start_time: blockStartTime,
        end_time: blockEndTime,
      });
      toast.success("Time blocked successfully");
      setBlockTitle("");
      setBlockDialogOpen(false);
      loadBlockedTimes();
    } catch {
      toast.error("Failed to block time");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await api.deleteBlockedTime(blockId);
      toast.success("Block removed");
      loadBlockedTimes();
    } catch {
      toast.error("Failed to remove block");
    }
  };

  const goFilter = (f) => navigate(f === "all" ? "/units" : `/units?status=${f}`);

  return (
    <div className="min-h-screen bg-slate-50 w-full max-w-full overflow-x-hidden">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm w-full max-w-full">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle={stats ? `${stats.total} units · ${stats.sites.length} site${stats.sites.length === 1 ? "" : "s"}` : "Loading..."} />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6 w-full max-w-full overflow-hidden" data-testid="overview-page">
        {/* Top welcome & Move in action */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
          <h1 className="font-display text-lg font-bold text-slate-900 leading-tight min-w-0">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            {stats && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                · <span className="font-semibold text-green-700">{stats.vacant}</span> vacant · <span className="font-semibold text-blue-700">{stats.occupancy_rate}%</span> occupancy
              </span>
            )}
          </h1>
          <MoveInDialog
            sites={stats?.sites || []}
            restrictedKey={restrictedKey}
            onDone={() => { loadStats(); loadWaitlist(); }}
            triggerClassName="h-10 px-5 gap-2 bg-green-600 border-green-600 text-white hover:bg-green-700 hover:text-white shadow-sm"
          />
        </div>

        {overdue && (overdue.overdue_count > 0 || overdue.unmatched_count > 0 || overdue.locked_count > 0 || overdue.restore_count > 0) && (
          <button onClick={() => navigate("/overdue")} className="w-full text-left rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 hover:bg-red-100 transition-colors" data-testid="overview-overdue-banner">
            <span className="font-bold text-red-700" data-testid="overview-overdue-count">{overdue.overdue_count} tenant{overdue.overdue_count === 1 ? "" : "s"} overdue{overdue.unmatched_count ? ` + ${overdue.unmatched_count} unmatched` : ""}</span>
            {overdue.locked_count > 0 && <span className="text-sm text-red-700 flex items-center gap-1" data-testid="overview-locked-count"><Lock className="h-3.5 w-3.5" /> {overdue.locked_count} locked out</span>}
            {overdue.restore_count > 0 && <span className="text-sm font-semibold text-green-700" data-testid="overview-restore-count">{overdue.restore_count} paid — restore gate access</span>}
            <span className="ml-auto text-sm text-red-600 underline">View list</span>
          </button>
        )}

        {/* KPI cards */}
        <KpiStrip
          stats={stats}
          activeFilter={null}
          onFilter={goFilter}
          onMeterLogs={() => navigate("/meter-logs")}
        />

        {/* Weekly meetings calendar */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="meetings-calendar">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" /> Meetings Calendar
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={prevWeek} data-testid="calendar-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium text-slate-700 min-w-[150px] text-center" data-testid="calendar-week-label">
                {weekDays[0].toLocaleDateString("en-NZ", { day: "2-digit", month: "short" })} – {weekDays[6].toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
              <Button size="sm" variant="outline" onClick={nextWeek} data-testid="calendar-next-week"><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setCurrentWeekBase(new Date())} data-testid="calendar-today">Today</Button>
              <Button size="sm" className="gap-1 bg-slate-900 hover:bg-slate-800" onClick={() => setBlockDialogOpen(true)} data-testid="calendar-block-time-btn">
                <Lock className="h-3.5 w-3.5" /> Block Time
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed border-collapse text-xs" data-testid="calendar-grid">
              <thead>
                <tr>
                  <th className="w-20 p-2 text-left font-medium text-slate-400"><Clock className="h-3.5 w-3.5" /></th>
                  {weekDays.map((d) => {
                    const ds = formatDateYMD(d);
                    return (
                      <th key={ds} className={cn("p-2 text-center font-semibold border-b border-slate-200", ds === todayStr ? "text-blue-700 bg-blue-50" : "text-slate-600")}>
                        {d.toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "short" })}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {TIME_SLOTS.map((slot) => (
                  <tr key={slot}>
                    <td className="p-1.5 font-mono-nums text-slate-400 border-t border-slate-100 whitespace-nowrap">{slot}</td>
                    {weekDays.map((d) => {
                      const ds = formatDateYMD(d);
                      const meetings = waitlistEntries.filter((e) => e.desired_date === ds && e.meeting_time === slot && e.status !== "cancelled");
                      const block = blockedSlots.find((b) => blockCovers(b, ds, slot));
                      return (
                        <td key={ds} onClick={() => { if (!block) { setQuickAdd({ desired_date: ds, meeting_time: slot }); } }} data-testid={`calendar-slot-${ds}-${slot.replace(/[^0-9APM]/g, "")}`} className={cn("p-1 align-top border-t border-l border-slate-100 h-9", ds === todayStr && "bg-blue-50/40", block ? "bg-slate-100" : "cursor-pointer hover:bg-blue-50")}>
                          {block && (
                            <div className="flex items-center justify-between gap-1 rounded bg-slate-300/70 px-1.5 py-0.5 text-slate-700" data-testid={`calendar-block-${block.id}-${slot}`}>
                              <span className="truncate flex items-center gap-1"><Lock className="h-3 w-3 shrink-0" />{block.title}</span>
                              {block.start_time === slot && (
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.id); }} className="text-slate-500 hover:text-red-600" data-testid={`calendar-delete-block-${block.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                          {meetings.map((m) => (
                            <button
                              key={m.id}
                              onClick={(e) => { e.stopPropagation(); setDetailEntry(m); }}
                              className="mt-0.5 w-full truncate rounded bg-blue-600 px-1.5 py-0.5 text-left text-white hover:bg-blue-700"
                              data-testid={`calendar-meeting-${m.id}`}
                            >
                              {m.customer_name}{m.preferred_size ? ` · ${m.preferred_size}` : ""}
                            </button>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent data-testid="block-time-dialog">
          <DialogHeader><DialogTitle>Block Time</DialogTitle></DialogHeader>
          <form onSubmit={handleAddBlock} className="space-y-3">
            <Input placeholder="Reason (e.g. Away, Lunch)" value={blockTitle} onChange={(e) => setBlockTitle(e.target.value)} data-testid="block-title-input" required />
            <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="block-date-input" required />
            <div className="grid grid-cols-2 gap-3">
              <select value={blockStartTime} onChange={(e) => setBlockStartTime(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="block-start-select">
                {TIME_SLOTS.map((t) => <option key={t} value={t}>From {t}</option>)}
              </select>
              <select value={blockEndTime} onChange={(e) => setBlockEndTime(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="block-end-select">
                {TIME_SLOTS.map((t) => <option key={t} value={t}>Through {t}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBlockDialogOpen(false)} data-testid="block-cancel-btn">Cancel</Button>
              <Button type="submit" className="gap-1" data-testid="block-save-btn"><Plus className="h-4 w-4" /> Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <EnquiryDialog sizes={sizes} open={!!quickAdd} onOpenChange={(o) => { if (!o) { setQuickAdd(null); } }} initial={quickAdd} onDone={() => { setQuickAdd(null); loadWaitlist(); }} />
      <EnquiryDetailDialog entry={detailEntry} open={!!detailEntry} onOpenChange={(o) => { if (!o) { setDetailEntry(null); } }} sizes={sizes} onSaved={() => { loadWaitlist(); loadStats(); }} />
    </div>
  );
}
